import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectClient, selectSession, selectSessionAdvisor } from '@client/model';
import { SessionType } from '@shared';
import { useSession, useSessionMutations } from '@/features/guided-financial-planning/context';
import { buildPolicyFundingSource } from '@/features/guided-financial-planning/utils';
import { getQuoteId, type GuidedPlanningQuote } from './use-quotes-display';
import { getFundingAllocationActions } from '@/features/guided-financial-planning/stores';

interface SelectedQuoteInfo {
  quoteId: string;
  quote: GuidedPlanningQuote;
  policyFundingSource: PendingPolicyFundingSource;
}

interface UseAddPolicyFromQuoteResult {
  /** The first selected quote (for single-selection mode) */
  selectedQuote: SelectedQuoteInfo | null;
  /** Check if a specific quote is already added */
  isQuoteAdded: (quote: GuidedPlanningQuote) => boolean;
  /** Add a quote to the session */
  addQuote: (quote: GuidedPlanningQuote) => string | null;
  /** Remove a quote from the session */
  removeQuote: (quote: GuidedPlanningQuote) => void;
}

/**
 * Hook to manage quote selection in the guided planning session.
 *
 * Stores quote data and pre-built PolicyFundingSource in session.stepData.selectedQuotes.
 * The policy is only persisted to the client when the session is finalized.
 */
export function useAddPolicyFromQuote(): UseAddPolicyFromQuoteResult {
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const advisor = useSelector(selectSessionAdvisor);
  const { session } = useSession();
  const { updateStepData } = useSessionMutations();

  const isAdvisorSession =
    sessionType === SessionType.advisor || sessionType === SessionType.wizard;

  const selectedQuote = useMemo((): SelectedQuoteInfo | null => {
    const selectedQuotes = (session?.stepData?.selectedQuotes ?? {}) as Record<
      string,
      SelectedQuoteData
    >;
    const entries = Object.entries(selectedQuotes);
    if (entries.length === 0) return null;

    const [quoteId, data] = entries[0];
    return {
      quoteId,
      quote: data.quote as GuidedPlanningQuote,
      policyFundingSource: data.policyFundingSource,
    };
  }, [session?.stepData?.selectedQuotes]);

  const isQuoteAdded = (quote: GuidedPlanningQuote): boolean => {
    const quoteId = getQuoteId(quote);
    const selectedQuotes = session?.stepData?.selectedQuotes ?? {};
    return quoteId in selectedQuotes;
  };

  const addQuote = (quote: GuidedPlanningQuote): string | null => {
    if (!client?.clientId) {
      console.warn('Cannot add policy: client not found');
      return null;
    }

    if (!session?.id) {
      console.warn('Cannot add policy: no active guided planning session');
      return null;
    }

    const quoteId = getQuoteId(quote);

    if (isQuoteAdded(quote)) {
      return quoteId;
    }

    const policyFundingSource = buildPolicyFundingSource({
      quote,
      sessionId: session.id,
      client,
      isAdvisorSession,
      advisorId: advisor?.advisorId,
    });

    const currentSelectedQuotes = session.stepData?.selectedQuotes ?? {};
    const currentAllocations = session.stepData?.policyFundingAllocations ?? {};

    updateStepData({
      selectedQuotes: {
        ...currentSelectedQuotes,
        [quoteId]: {
          quoteId,
          quote,
          policyFundingSource,
        },
      },
      policyFundingAllocations: {
        ...currentAllocations,
        [quoteId]: {},
      },
    });

    getFundingAllocationActions().initFromSession({
      ...currentAllocations,
      [quoteId]: {},
    });

    return quoteId;
  };

  const removeQuote = (quote: GuidedPlanningQuote): void => {
    const quoteId = getQuoteId(quote);
    const currentSelectedQuotes = { ...(session?.stepData?.selectedQuotes ?? {}) };
    const currentAllocations = { ...(session?.stepData?.policyFundingAllocations ?? {}) };

    delete currentSelectedQuotes[quoteId];
    delete currentAllocations[quoteId];

    updateStepData({
      selectedQuotes: currentSelectedQuotes,
      policyFundingAllocations: currentAllocations,
    });

    getFundingAllocationActions().initFromSession(currentAllocations);
  };

  return {
    selectedQuote,
    isQuoteAdded,
    addQuote,
    removeQuote,
  };
}
